import type { Agrupador, CobroPendiente, ItemCobroPendiente, Moneda, NodoArbol } from '../types';

export const MONEDAS: Moneda[] = ['ARS', 'USD'];

export interface OpcionAgrupador {
  id: number;
  nombre: string;
  profundidad: number;
}

function hijosPorPadre(lista: Agrupador[]): Map<number | null, Agrupador[]> {
  const mapa = new Map<number | null, Agrupador[]>();
  for (const a of lista) {
    const hermanos = mapa.get(a.parentId) ?? [];
    hermanos.push(a);
    mapa.set(a.parentId, hermanos);
  }
  for (const hermanos of mapa.values()) {
    hermanos.sort((x, y) => x.orden - y.orden || x.nombre.localeCompare(y.nombre));
  }
  return mapa;
}

/**
 * Lista del árbol en preorden con su profundidad, para mostrarlo indentado o
 * como opciones de un select. `excluirId` saca ese agrupador y sus
 * descendientes (no pueden ser su propio padre).
 */
export function aplanarAgrupadores(lista: Agrupador[], excluirId?: number): OpcionAgrupador[] {
  const hijos = hijosPorPadre(lista);
  const resultado: OpcionAgrupador[] = [];

  const recorrer = (parentId: number | null, profundidad: number) => {
    for (const a of hijos.get(parentId) ?? []) {
      if (a.id === excluirId) continue;
      resultado.push({ id: a.id, nombre: a.nombre, profundidad });
      recorrer(a.id, profundidad + 1);
    }
  };
  recorrer(null, 0);

  return resultado;
}

/** "Impuestos › Sellos" */
export function rutaAgrupador(lista: Agrupador[], id: number): string {
  const porId = new Map(lista.map((a) => [a.id, a]));
  const nombres: string[] = [];
  let actual = porId.get(id);
  while (actual) {
    nombres.unshift(actual.nombre);
    actual = actual.parentId === null ? undefined : porId.get(actual.parentId);
  }
  return nombres.join(' › ');
}

/** Monedas en las que el nodo (con sus descendientes) tiene ítems pendientes. */
export function monedasDelNodo(nodo: NodoArbol<unknown>, moneda: Moneda | 'todas'): Moneda[] {
  return MONEDAS.filter((m) => (moneda === 'todas' || m === moneda) && nodo.totales[m].cantidad > 0);
}

/** Lo que tienen en común los ítems de egresos y de cobros para filtrarlos. */
interface ItemBuscable {
  itemNombre: string;
  clienteNombre: string;
  tramitePropiedad?: string;
  tramiteId: number | null;
  moneda: Moneda;
}

function itemCoincide(item: ItemBuscable, q: string): boolean {
  return (
    item.itemNombre.toLowerCase().includes(q) ||
    item.clienteNombre.toLowerCase().includes(q) ||
    (item.tramitePropiedad ?? '').toLowerCase().includes(q) ||
    String(item.tramiteId ?? '').includes(q)
  );
}

/**
 * Poda el árbol para mostrar: deja los nodos con pendientes en la moneda
 * elegida y, si hay búsqueda, los que coinciden por nombre (con todo su
 * subárbol) o tienen ítems que coinciden. Los totales no se recalculan: son
 * los del agrupador completo, que es lo que se paga.
 */
export function filtrarArbol<TItem extends ItemBuscable>(
  nodos: NodoArbol<TItem>[],
  { moneda, search }: { moneda: Moneda | 'todas'; search: string },
): NodoArbol<TItem>[] {
  const q = search.trim().toLowerCase();

  const filtrar = (nodo: NodoArbol<TItem>, ancestroCoincide: boolean): NodoArbol<TItem> | null => {
    if (monedasDelNodo(nodo, moneda).length === 0) return null;

    const coincide = ancestroCoincide || !q || nodo.nombre.toLowerCase().includes(q);
    const items = nodo.items.filter(
      (i) => (moneda === 'todas' || i.moneda === moneda) && (coincide || itemCoincide(i, q)),
    );
    const hijos = nodo.hijos
      .map((h) => filtrar(h, coincide))
      .filter((h): h is NodoArbol<TItem> => h !== null);

    if (!coincide && items.length === 0 && hijos.length === 0) return null;
    return { ...nodo, items, hijos };
  };

  return nodos.map((n) => filtrar(n, false)).filter((n): n is NodoArbol<TItem> => n !== null);
}

/** El nodo con ese id en el árbol completo (sin filtrar). */
export function buscarNodo<TItem>(nodos: NodoArbol<TItem>[], id: number): NodoArbol<TItem> | undefined {
  for (const nodo of nodos) {
    if (nodo.id === id) return nodo;
    const encontrado = buscarNodo(nodo.hijos, id);
    if (encontrado) return encontrado;
  }
  return undefined;
}

export interface ClienteDelNodo {
  clave: string;
  nombre: string;
  total: number;
  cobrado: number;
  saldo: number;
  cantidad: number;
}

/**
 * Clientes con ítems pendientes en el nodo y sus descendientes, en una moneda,
 * para cobrarle un agrupador a uno solo. Pasar el nodo sin filtrar: con
 * búsqueda, el árbol filtrado no tiene todos los ítems.
 */
export function clientesDelNodo(nodo: NodoArbol<ItemCobroPendiente>, moneda: Moneda): ClienteDelNodo[] {
  const porClave = new Map<string, ClienteDelNodo>();
  const centavos = (v: number) => Math.round(v * 100);

  const recorrer = (n: NodoArbol<ItemCobroPendiente>) => {
    for (const item of n.items) {
      if (item.moneda !== moneda) continue;
      const cliente = porClave.get(item.clienteClave)
        ?? { clave: item.clienteClave, nombre: item.clienteNombre || `Trámite #${item.tramiteId}`, total: 0, cobrado: 0, saldo: 0, cantidad: 0 };
      cliente.total += centavos(item.total);
      cliente.cobrado += centavos(item.totalCobrado);
      cliente.saldo += centavos(item.saldoRestante);
      cliente.cantidad += 1;
      porClave.set(item.clienteClave, cliente);
    }
    n.hijos.forEach(recorrer);
  };
  recorrer(nodo);

  return [...porClave.values()]
    .map((c) => ({ ...c, total: c.total / 100, cobrado: c.cobrado / 100, saldo: c.saldo / 100 }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
}

/**
 * Los otros trámites no tienen ítems de presupuesto ni agrupador: se muestran
 * como un nodo aparte (sin cobro en bloque) con un ítem por trámite y moneda.
 */
export function nodoOtrosTramites(cobros: CobroPendiente[]): NodoArbol<CobroPendiente> {
  const totales = Object.fromEntries(MONEDAS.map((m) => {
    const deMoneda = cobros.filter((c) => c.moneda === m);
    const suma = (f: (c: CobroPendiente) => number) => Math.round(deMoneda.reduce((acc, c) => acc + f(c) * 100, 0)) / 100;
    return [m, {
      total: suma((c) => c.total),
      pagado: suma((c) => c.totalCobrado),
      saldo: suma((c) => c.saldoRestante),
      cantidad: deMoneda.length,
      clientes: new Set(deMoneda.map((c) => c.clienteNombre)).size,
    }];
  })) as NodoArbol<CobroPendiente>['totales'];

  return { id: null, nombre: 'Otros Trámites', parentId: null, totales, items: cobros, hijos: [] };
}
